// A6 — adapter for /v1/payroll-tr/workplaces + …/employees/:id/master-data.
// Backend contract: services/platform-graph/src/routes/payroll-tr-master-data.ts.
//
// Every call here is payroll-admin gated on the server (`create` tier), NOT the
// employee_self `payroll.read` the payslip endpoints use — these responses carry
// national IDs and salary IBANs.

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope } from "@/api/errors";

/** Mirrors the route's enum; `noDiscount` is what a workplace starts at. */
export type EmployerSector = "manufacturing" | "other" | "noDiscount";

/** Which gross the TİS dues rate multiplies (SEM-11199). */
export type UnionDuesBase = "bare_gross" | "dressed_gross";

/** The employee's side of the dues; the rate lives on the workplace. */
export type UnionStatus = "member" | "non_member" | "out_of_scope";

export interface WorkplaceRow {
  readonly workplace_id: string;
  readonly legal_entity_id: string;
  readonly location_id: string | null;
  readonly name: string;
  readonly sgk_registration_number: string;
  readonly tax_office_name: string;
  readonly is_default: boolean;
  // The employer-level statutory facts the payroll engine reads (SEM-11132).
  // They belong to the SGK sicil, which is why they are edited here and not on
  // the run screen.
  readonly employer_sector: EmployerSector;
  readonly tis_workplace: boolean;
  readonly minimum_wage_support_eligible: boolean;
  readonly employer_sgk_discount_eligible: boolean;
  readonly turizm_konaklama_support_eligible: boolean;
  // 4857 m.46 (7553 s.K.) turizm işletmesi belgesi window, YYYY-MM-DD. A null
  // start means no certificate, and a weekly-rest deferral on calculate is refused.
  readonly tourism_certificate_valid_from: string | null;
  readonly tourism_certificate_valid_to: string | null;
  // Decimal ratio STRINGS: the column is NUMERIC(9,6) and a rate that made a
  // round trip through a JS number would come back a different withholding.
  readonly union_dues_rate_ratio: string;
  readonly solidarity_dues_rate_ratio: string;
  readonly union_dues_base: UnionDuesBase;
  readonly lifecycle_state: string;
}

/** Mirrors the route's enum; KVKK m.6 health data, never logged. */
export type DisabilityDegree = "none" | "first" | "second" | "third";

export interface EmployeeMasterData {
  readonly employee_id: string;
  readonly tckn: string | null;
  readonly education_level: string | null;
  readonly payroll_workplace_id: string | null;
  readonly has_multiple_employers: boolean;
  readonly is_highest_paying_employer: boolean;
  readonly is_retired: boolean;
  readonly disability_degree: DisabilityDegree;
  readonly union_status: UnionStatus;
  /** 4857 m.46 written consent day and the day a withdrawal takes effect. */
  readonly weekly_rest_deferral_consent_date: string | null;
  readonly weekly_rest_deferral_withdrawn_from: string | null;
  /**
   * TODO(deferred-symbol: apps/customer-web/src/api/schema.generated.ts#/v1/employments)
   * Which employment applies the asgari ucret istisnasi (SEM-6817). Read-only on
   * this screen so far: there is no employments list endpoint to pick from yet,
   * and the write body must still carry the server value or the PUT — a full
   * replacement — would silently clear the assignment.
   */
  readonly minimum_wage_exemption_employment_id: string | null;
  readonly bank_account: {
    readonly iban: string;
    readonly bank_name: string | null;
    readonly branch_name: string | null;
  } | null;
}

export interface WorkplaceWriteBody {
  readonly legal_entity_id: string;
  readonly name: string;
  readonly sgk_registration_number: string;
  readonly tax_office_name: string;
  readonly is_default: boolean;
  readonly employer_sector: EmployerSector;
  readonly tis_workplace: boolean;
  readonly minimum_wage_support_eligible: boolean;
  readonly employer_sgk_discount_eligible: boolean;
  readonly turizm_konaklama_support_eligible: boolean;
  readonly tourism_certificate_valid_from: string | null;
  readonly tourism_certificate_valid_to: string | null;
  readonly union_dues_rate_ratio: string;
  readonly solidarity_dues_rate_ratio: string;
  readonly union_dues_base: UnionDuesBase;
}

export interface LegalEntityOption {
  readonly legal_entity_id: string;
  readonly name: string;
  /** Shown beside the name so an admin sees WHICH number will print. */
  readonly vkn: string | null;
}

export async function fetchLegalEntities(): Promise<readonly LegalEntityOption[]> {
  const data = await unwrapEnvelope(platformGraph.GET("/v1/payroll-tr/legal-entities", {}));
  return (data as { items: readonly LegalEntityOption[] }).items;
}

export async function fetchWorkplaces(): Promise<readonly WorkplaceRow[]> {
  const data = await unwrapEnvelope(platformGraph.GET("/v1/payroll-tr/workplaces", {}));
  return (data as { items: readonly WorkplaceRow[] }).items;
}

export async function createWorkplace(body: WorkplaceWriteBody): Promise<WorkplaceRow> {
  const data = await unwrapEnvelope(
    platformGraph.POST("/v1/payroll-tr/workplaces", { body } as never),
  );
  return (data as { workplace: WorkplaceRow }).workplace;
}

export async function updateWorkplace(
  workplaceId: string,
  body: Partial<WorkplaceWriteBody> & { readonly lifecycle_state?: "active" | "archived" },
): Promise<WorkplaceRow> {
  const data = await unwrapEnvelope(
    platformGraph.PATCH("/v1/payroll-tr/workplaces/{id}", {
      params: { path: { id: workplaceId } },
      body,
    } as never),
  );
  return (data as { workplace: WorkplaceRow }).workplace;
}

export async function fetchEmployeeMasterData(employeeId: string): Promise<EmployeeMasterData> {
  const data = await unwrapEnvelope(
    platformGraph.GET("/v1/payroll-tr/employees/{employeeId}/master-data", {
      params: { path: { employeeId } },
    }),
  );
  return (data as { master_data: EmployeeMasterData }).master_data;
}

export interface MasterDataWriteBody {
  readonly tckn: string | null;
  readonly education_level: string | null;
  readonly payroll_workplace_id: string | null;
  readonly has_multiple_employers: boolean;
  readonly is_highest_paying_employer: boolean;
  readonly is_retired: boolean;
  readonly disability_degree: DisabilityDegree;
  readonly union_status: UnionStatus;
  readonly weekly_rest_deferral_consent_date: string | null;
  readonly weekly_rest_deferral_withdrawn_from: string | null;
  /** Sent back unchanged; see EmployeeMasterData above. */
  readonly minimum_wage_exemption_employment_id: string | null;
  readonly bank_account: {
    readonly iban: string;
    readonly bank_name: string | null;
    readonly branch_name: string | null;
  } | null;
}

export async function saveEmployeeMasterData(
  employeeId: string,
  body: MasterDataWriteBody,
): Promise<EmployeeMasterData> {
  const data = await unwrapEnvelope(
    platformGraph.PUT("/v1/payroll-tr/employees/{employeeId}/master-data", {
      params: { path: { employeeId } },
      body,
    } as never),
  );
  return (data as { master_data: EmployeeMasterData }).master_data;
}
